import type {
  As,
  ImageProps,
  TagProps
} from '@chakra-ui/react'
import {
  Box,
  Heading,
  Image,
  LinkBox,
  LinkOverlay,
  Tag
} from '@chakra-ui/react'
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'

import carkulkaRouterImage from '../assets/images/carkulka-dashboard-image.webp'
import cestovkoRouterImage from '../assets/images/cestovko-router-image.jpg'
import chaloupkaRouterImage from '../assets/images/chaloupka-router-image.jpg'
import smeRouterImage from '../assets/images/sme-router-image.jpg'
import { useAuth } from '../context/AuthContext'
const businesskaUrl = import.meta.env.VITE_BUSINESSKA_URL

interface AppSelectorLinkProps {
  title: string
  to?: string
  href?: string
  imageSrc: string
  disabled?: boolean
  tag?: string | ReactNode
  tagColorScheme?: TagProps['colorScheme']
  as: As
  imageProps?: ImageProps
}

const AppSelectorLink = ({
  title,
  to,
  href,
  imageSrc,
  as,
  disabled = false,
  tag,
  tagColorScheme,
  imageProps,
}: AppSelectorLinkProps) => {
  return (
    <LinkBox
      sx={{
        display: 'grid',
        gap: 4,
        alignItems: 'center',
        bg: disabled ? 'gray.200' : 'white',
        p: 4,
        border: '1px solid',
        borderColor: 'gray.200',
        borderRadius: 'md',
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      <Image
        src={imageSrc}
        alt={title}
        w={320}
        style={{
          filter: disabled ? 'grayscale(1)' : 'none',
          mixBlendMode: 'darken',
        }}
        {...imageProps}
      />

      <Heading
        as="h2"
        sx={{
          m: 0,
          alignSelf: 'end',
          fontSize: { base: 'xl', lg: '3xl' },
          fontWeight: 'bold',
          letterSpacing: 'tighter',
          textAlign: 'center',
        }}
      >
        {disabled && <LinkOverlay>{title}</LinkOverlay>}

        {(to || href) && !disabled && (
          <LinkOverlay as={as} to={to} href={href}>
            {title}
          </LinkOverlay>
        )}
      </Heading>

      {tag && (
        <Tag
          sx={{
            position: 'absolute',
            top: '0',
            left: '0',
            transform: 'translate(-1px, -1px)',
            whiteSpace: 'nowrap',
          }}
          colorScheme={tagColorScheme || 'blue'}
        >
          {tag}
        </Tag>
      )}
    </LinkBox>
  )
}

export const AppSelector = () => {
  const { user } = useAuth() ?? {}
  const allowedCestovka = user?.travelRights ?? false

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        gap: { base: 2, xl: 8 },
      }}
    >
      {user?.vehicleRights && (
        <AppSelectorLink
          as={Link}
          title="CARkulka"
          to="/carkulka"
          imageSrc={carkulkaRouterImage}
          tagColorScheme="green"
          imageProps={{ maxW: '85%', alignSelf: 'end', mx: 'auto', mb: -4 }}
        />
      )}

      {user?.propertyRights && (
        <AppSelectorLink
          as={Link}
          title="CHALoupka"
          to="/chaloupka"
          imageSrc={chaloupkaRouterImage}
        />
      )}

      {allowedCestovka && (
        <AppSelectorLink
          as={Link}
          title="CEStovka"
          to="/cestovko"
          imageSrc={cestovkoRouterImage}
        />
      )}

      {!allowedCestovka && (
        <AppSelectorLink
          as={Link}
          title="CEStovka"
          to="/cestovko"
          imageSrc={cestovkoRouterImage}
          tag="Aplikaci připravujeme"
          tagColorScheme="blue"
          disabled={!allowedCestovka}
        />
      )}

      <AppSelectorLink
        as={'a'}
        href={businesskaUrl}
        title="BUSinesska"
        imageSrc={smeRouterImage}
      />
    </Box>
  )
}
